import { useEffect, useRef } from 'react';
import ReactDOM from 'react-dom';
import { X } from 'lucide-react';

/**
 * Modale générique — design inspiré Flowbite, calé sur les variables CSS Synolog.
 * Props :
 *  - open, onClose, title, children, footer
 *  - subtitle?    : ligne d'aide sous le titre
 *  - size?        : 'sm' | 'md' (défaut) | 'lg' | 'xl'
 *  - fill?        : le corps ne scrolle plus lui-même — il devient une colonne
 *                   flex dont l'enfant porte le scroll. Pour les modales dont
 *                   le contenu est une liste, afin de n'avoir qu'une seule
 *                   barre de défilement (ticket #51).
 *  - closeOnBackdrop? (défaut true)
 */
/**
 * Pile des modales ouvertes : Échap ne ferme que celle du dessus. Sans elle,
 * chaque modale écoute `keydown` sur `window` et une modale ouverte depuis une
 * autre (sélecteur Gescom depuis la fiche affaire…) les fermait toutes deux
 * d'un coup, en perdant la saisie de la première.
 */
const openStack = [];

export default function Modal({
  open,
  onClose,
  title,
  subtitle,
  children,
  footer,
  size = 'md',
  fill = false,
  closeOnBackdrop = true,
}) {
  const panelRef = useRef(null);
  // Le clic-pour-fermer ne doit se déclencher que si le geste COMMENCE ET FINIT
  // sur le backdrop lui-même. Sinon, une sélection de texte démarrée dans la
  // modale qui « déborde » jusqu'au backdrop (relâchée hors du panel) fermait la
  // fenêtre et perdait la saisie (ticket #12).
  const backdropMouseDownRef = useRef(false);
  // onClose dans une ref : le handler Escape reste à jour sans faire de
  // `onClose` une dépendance de l'effet (sinon, comme les appelants passent
  // souvent une fonction inline recréée à chaque render, l'effet rejouerait à
  // chaque frappe et revolerait le focus — cf. bug "le focus saute sur la
  // croix" lors de la saisie).
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;

  useEffect(() => {
    if (!open) return undefined;
    const token = {};
    openStack.push(token);
    const onKey = (e) => {
      if (e.key === 'Escape' && openStack[openStack.length - 1] === token) onCloseRef.current?.();
    };
    window.addEventListener('keydown', onKey);

    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    const previouslyFocused = document.activeElement;
    queueMicrotask(() => {
      const root = panelRef.current;
      if (!root) return;
      // Focus le premier champ de saisie du corps de la modale (jamais le
      // bouton ✕ du header, qui est pourtant 1er dans l'ordre du DOM).
      const body = root.querySelector('.modal-body') ?? root;
      const focusable = body.querySelector(
        'input:not([disabled]), select:not([disabled]), textarea:not([disabled]), button:not([disabled]), [tabindex]:not([tabindex="-1"])',
      );
      focusable?.focus?.();
    });

    return () => {
      const i = openStack.indexOf(token);
      if (i !== -1) openStack.splice(i, 1);
      window.removeEventListener('keydown', onKey);
      document.body.style.overflow = prevOverflow;
      if (previouslyFocused && typeof previouslyFocused.focus === 'function') {
        previouslyFocused.focus();
      }
    };
  }, [open]);

  if (!open) return null;

  return ReactDOM.createPortal(
    <div
      className="modal-backdrop"
      onMouseDown={(e) => {
        backdropMouseDownRef.current = e.target === e.currentTarget;
      }}
      onClick={
        closeOnBackdrop
          ? (e) => {
              if (e.target === e.currentTarget && backdropMouseDownRef.current) {
                onClose?.();
              }
              backdropMouseDownRef.current = false;
            }
          : undefined
      }
    >
      <div
        ref={panelRef}
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-label={typeof title === 'string' ? title : undefined}
        className={`modal-panel modal-panel--${size}${fill ? ' modal-panel--fill' : ''}`}
      >
        <header className="modal-header">
          <div style={{ minWidth: 0 }}>
            <h2 className="modal-title">{title}</h2>
            {subtitle ? <p className="modal-subtitle">{subtitle}</p> : null}
          </div>
          <button
            type="button"
            onClick={onClose}
            title="Fermer"
            aria-label="Fermer"
            className="modal-close"
          >
            <X size={18} />
          </button>
        </header>

        <div className="modal-body">{children}</div>

        {footer ? <footer className="modal-footer">{footer}</footer> : null}
      </div>
    </div>,
    document.body,
  );
}
